/**
 * Utility functions for item positioning, alignment, and distribution
 */

/**
 * Align items to the left edge of the leftmost item
 * @param {Array} items - Array of items to align
 * @returns {Array} Updated items with new positions
 */
export const alignLeft = (items) => {
  if (items.length === 0) return items;
  
  const leftmostX = Math.min(...items.map(item => item.x));
  
  return items.map(item => ({
    ...item,
    x: leftmostX
  }));
};

/**
 * Align items to the center of the bounding box
 * @param {Array} items - Array of items to align
 * @returns {Array} Updated items with new positions
 */
export const alignCenter = (items) => {
  if (items.length === 0) return items;
  
  const leftmost = Math.min(...items.map(item => item.x));
  const rightmost = Math.max(...items.map(item => item.x + item.width));
  const centerX = (leftmost + rightmost) / 2;
  
  return items.map(item => ({
    ...item,
    x: centerX - item.width / 2
  }));
};

/**
 * Align items to the right edge of the rightmost item
 * @param {Array} items - Array of items to align
 * @returns {Array} Updated items with new positions
 */
export const alignRight = (items) => {
  if (items.length === 0) return items;
  
  const rightmostX = Math.max(...items.map(item => item.x + item.width));
  
  return items.map(item => ({
    ...item,
    x: rightmostX - item.width
  }));
};

/**
 * Align items to the top edge of the topmost item
 * @param {Array} items - Array of items to align
 * @returns {Array} Updated items with new positions
 */
export const alignTop = (items) => {
  if (items.length === 0) return items;
  
  const topmostY = Math.min(...items.map(item => item.y));
  
  return items.map(item => ({
    ...item,
    y: topmostY
  }));
};

/**
 * Align items to the middle of the bounding box
 * @param {Array} items - Array of items to align
 * @returns {Array} Updated items with new positions
 */
export const alignMiddle = (items) => {
  if (items.length === 0) return items;
  
  const topmost = Math.min(...items.map(item => item.y));
  const bottommost = Math.max(...items.map(item => item.y + item.height));
  const centerY = (topmost + bottommost) / 2;
  
  return items.map(item => ({
    ...item,
    y: centerY - item.height / 2
  }));
};

/**
 * Align items to the bottom edge of the bottommost item
 * @param {Array} items - Array of items to align
 * @returns {Array} Updated items with new positions
 */
export const alignBottom = (items) => {
  if (items.length === 0) return items;
  
  const bottommostY = Math.max(...items.map(item => item.y + item.height));
  
  return items.map(item => ({
    ...item,
    y: bottommostY - item.height
  }));
};

/**
 * Distribute items evenly horizontally
 * @param {Array} items - Array of items to distribute
 * @returns {Array} Updated items with new positions
 */
export const distributeHorizontally = (items) => {
  if (items.length < 3) return items;
  
  // Sort items by x position
  const sortedItems = [...items].sort((a, b) => a.x - b.x);
  
  const leftmost = sortedItems[0];
  const rightmost = sortedItems[sortedItems.length - 1];
  
  const totalWidth = (rightmost.x + rightmost.width) - leftmost.x;
  const totalItemWidth = sortedItems.reduce((sum, item) => sum + item.width, 0);
  const availableSpace = totalWidth - totalItemWidth;
  const spacing = availableSpace / (sortedItems.length - 1);
  
  let currentX = leftmost.x;
  
  return items.map(item => {
    const sortedIndex = sortedItems.findIndex(sorted => sorted.id === item.id);
    if (sortedIndex === 0) {
      return item; // Keep leftmost item in place
    } else if (sortedIndex === sortedItems.length - 1) {
      return item; // Keep rightmost item in place
    } else {
      // Calculate new position
      let newX = leftmost.x;
      for (let i = 0; i < sortedIndex; i++) {
        newX += sortedItems[i].width + spacing;
      }
      return {
        ...item,
        x: newX
      };
    }
  });
};

/**
 * Distribute items evenly vertically
 * @param {Array} items - Array of items to distribute
 * @returns {Array} Updated items with new positions
 */
export const distributeVertically = (items) => {
  if (items.length < 3) return items;
  
  // Sort items by y position
  const sortedItems = [...items].sort((a, b) => a.y - b.y);
  
  const topmost = sortedItems[0];
  const bottommost = sortedItems[sortedItems.length - 1];
  
  const totalHeight = (bottommost.y + bottommost.height) - topmost.y;
  const totalItemHeight = sortedItems.reduce((sum, item) => sum + item.height, 0);
  const availableSpace = totalHeight - totalItemHeight;
  const spacing = availableSpace / (sortedItems.length - 1);
  
  return items.map(item => {
    const sortedIndex = sortedItems.findIndex(sorted => sorted.id === item.id);
    if (sortedIndex === 0) {
      return item; // Keep topmost item in place
    } else if (sortedIndex === sortedItems.length - 1) {
      return item; // Keep bottommost item in place
    } else {
      // Calculate new position
      let newY = topmost.y;
      for (let i = 0; i < sortedIndex; i++) {
        newY += sortedItems[i].height + spacing;
      }
      return {
        ...item,
        y: newY
      };
    }
  });
};

/**
 * Snap position to grid
 * @param {number} position - Position to snap
 * @param {number} gridSize - Grid size in pixels
 * @returns {number} Snapped position
 */
export const snapToGrid = (position, gridSize) => {
  return Math.round(position / gridSize) * gridSize;
};

/**
 * Snap item to grid
 * @param {Object} item - Item to snap
 * @param {number} gridSize - Grid size in pixels
 * @returns {Object} Item with snapped position
 */
export const snapItemToGrid = (item, gridSize) => {
  return {
    ...item,
    x: snapToGrid(item.x, gridSize),
    y: snapToGrid(item.y, gridSize)
  };
};

/**
 * Get bounding box of multiple items
 * @param {Array} items - Array of items
 * @returns {Object} Bounding box with left, top, right, bottom, width, height
 */
export const getBoundingBox = (items) => {
  if (items.length === 0) {
    return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  }
  
  const left = Math.min(...items.map(item => item.x));
  const top = Math.min(...items.map(item => item.y));
  const right = Math.max(...items.map(item => item.x + item.width));
  const bottom = Math.max(...items.map(item => item.y + item.height));
  
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top
  };
};

/**
 * Check if two rectangles overlap
 * @param {Object} rect1 - First rectangle
 * @param {Object} rect2 - Second rectangle
 * @returns {boolean} True if rectangles overlap
 */
export const rectanglesOverlap = (rect1, rect2) => {
  return !(
    rect1.x + rect1.width < rect2.x ||
    rect2.x + rect2.width < rect1.x ||
    rect1.y + rect1.height < rect2.y ||
    rect2.y + rect2.height < rect1.y
  );
};

/**
 * Find overlapping items
 * @param {Object} item - Item to check
 * @param {Array} otherItems - Other items to check against
 * @returns {Array} Array of overlapping items
 */
export const findOverlappingItems = (item, otherItems) => {
  return otherItems.filter(otherItem => 
    otherItem.id !== item.id && rectanglesOverlap(item, otherItem)
  );
};

/**
 * Calculate snap positions for an item being moved
 * @param {Object} movingItem - The item being moved
 * @param {Array} otherItems - Other items on the canvas
 * @param {Object} container - Container dimensions
 * @param {number} threshold - Snap threshold in pixels
 * @returns {Object} Snap information
 */
export const calculateSnapPositions = (movingItem, otherItems, container, threshold = 10) => {
  const snapLines = [];
  const snapPositions = { x: null, y: null };
  
  if (!movingItem || !container) {
    return { snapLines, snapPositions };
  }

  const movingRect = {
    left: movingItem.x,
    right: movingItem.x + movingItem.width,
    top: movingItem.y,
    bottom: movingItem.y + movingItem.height,
    centerX: movingItem.x + movingItem.width / 2,
    centerY: movingItem.y + movingItem.height / 2
  };

  // Container center lines
  const containerCenterX = container.width / 2;
  const containerCenterY = container.height / 2;

  // Check snap to container center
  if (Math.abs(movingRect.centerX - containerCenterX) < threshold) {
    snapLines.push({ type: 'vertical', position: containerCenterX });
    snapPositions.x = containerCenterX - movingItem.width / 2;
  }

  if (Math.abs(movingRect.centerY - containerCenterY) < threshold) {
    snapLines.push({ type: 'horizontal', position: containerCenterY });
    snapPositions.y = containerCenterY - movingItem.height / 2;
  }

  // Check snap to other items
  otherItems.forEach(item => {
    if (item.id === movingItem.id) return;

    const itemRect = {
      left: item.x,
      right: item.x + item.width,
      top: item.y,
      bottom: item.y + item.height,
      centerX: item.x + item.width / 2,
      centerY: item.y + item.height / 2
    };

    // Vertical alignment checks
    // Left edges
    if (Math.abs(movingRect.left - itemRect.left) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.left });
      snapPositions.x = itemRect.left;
    }
    // Right edges
    if (Math.abs(movingRect.right - itemRect.right) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.right });
      snapPositions.x = itemRect.right - movingItem.width;
    }
    // Centers
    if (Math.abs(movingRect.centerX - itemRect.centerX) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.centerX });
      snapPositions.x = itemRect.centerX - movingItem.width / 2;
    }
    // Left to right edge
    if (Math.abs(movingRect.left - itemRect.right) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.right });
      snapPositions.x = itemRect.right;
    }
    // Right to left edge
    if (Math.abs(movingRect.right - itemRect.left) < threshold) {
      snapLines.push({ type: 'vertical', position: itemRect.left });
      snapPositions.x = itemRect.left - movingItem.width;
    }

    // Horizontal alignment checks
    // Top edges
    if (Math.abs(movingRect.top - itemRect.top) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.top });
      snapPositions.y = itemRect.top;
    }
    // Bottom edges
    if (Math.abs(movingRect.bottom - itemRect.bottom) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.bottom });
      snapPositions.y = itemRect.bottom - movingItem.height;
    }
    // Centers
    if (Math.abs(movingRect.centerY - itemRect.centerY) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.centerY });
      snapPositions.y = itemRect.centerY - movingItem.height / 2;
    }
    // Top to bottom edge
    if (Math.abs(movingRect.top - itemRect.bottom) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.bottom });
      snapPositions.y = itemRect.bottom;
    }
    // Bottom to top edge
    if (Math.abs(movingRect.bottom - itemRect.top) < threshold) {
      snapLines.push({ type: 'horizontal', position: itemRect.top });
      snapPositions.y = itemRect.top - movingItem.height;
    }
  });

  return { snapLines, snapPositions };
};